/** @jsx jsx */
import {
  Box,
  CardHorizontalDivider,
  Container,
  Flex,
  Grid,
  Heading,
  Paragraph,
  PopoverDisclosure,
  usePopover,
} from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { FC, RefObject, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { LazyUserAvatar } from "@/components/avatars";
import { PreviewFileEntry } from "@/components/Files/preview/PreviewFileEntry";
import { UserPopover } from "@/components/popovers/UserPopover";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { fetchConsultantProfile } from "@/data/consultant-profiles";
import { ConsultantProfile } from "@/data/consultant-profiles/types/ConsultantProfile";
import { confirmJobApplication } from "@/data/job-applications";
import { maxJobApplicationStepKey } from "@/data/job-applications/utils/maxJobApplicationStepKey";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { formatMultipleLocations } from "@/data/locations";
import { formatFullName } from "@/data/persons";
import { refresh } from "@/data/session";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFormats } from "@/hooks/Formats";
import { useToasts } from "@/views/components/Toast/useToasts";
import { SingleAnswer } from "@/views/consultant-profiles/details/questionnaire/SingleAnswer";
import { SingleWorkStyle } from "@/views/consultant-profiles/details/work-styles/SingleWorkStyle";
import { WORKSTYLES_ICON_MAP } from "@/views/ConsultantProfiles/Detail/WorkStylesSection";
import { JobApplicationPaymentDetailsSummary } from "@/views/job-applications/_components/JobApplicationPaymentDetailsSummary";
import { useApplyWizard } from "@/views/job-applications/apply/_components/ApplyWizardContext";
import {
  ApplySummaryForm,
  summaryCompanyValidation,
  SummaryFormValues,
  summaryValidation,
  transformDraftWithSummary,
} from "@/views/job-applications/apply/_summary/ApplySummaryForm";

import { ApplyWizardBackButton } from "../_components/ApplyWizardBackButton";

interface ApplyWizardSummaryProps {
  jobRequest: JobRequest;
  modalRef: RefObject<HTMLDivElement>;
}

export const ApplyWizardSummary: FC<ApplyWizardSummaryProps> = ({ jobRequest, modalRef }) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();
  const { addToast } = useToasts();
  const { contextCompany } = useContext(UserContext);
  const { updateContextUser } = useContext(UserContext);
  const { sendEvent } = useAnalytics();

  const { draft, setDraft, profile, initialProfile } = useApplyWizard();

  const { formatDate } = useFormats();

  const contactPersonPopoverState = usePopover();

  const [originalProfile, setOriginalProfile] = useState<ConsultantProfile>();
  const [contactPersonOriginalProfile, setContactPersonOriginalProfile] = useState<ConsultantProfile | "NONE">();

  const paymentRef = useRef<HTMLDivElement>(null);
  const [paymentError, setPaymentError] = useState<string>();

  const cvsChanged = draft?.consultantProfile.cvs.length === 1 && !draft!.consultantProfile.cvs[0].parentId;

  const availabilityChanged =
    (originalProfile &&
      (draft!.consultantProfile.availability !== originalProfile.availability ||
        draft!.consultantProfile.availabilityDate !== originalProfile.availabilityDate)) ??
    false;

  const contactPersonPhoneNumberChanged =
    (contactPersonOriginalProfile &&
      contactPersonOriginalProfile !== "NONE" &&
      (contactPersonOriginalProfile.phone?.phoneCode !== draft!.consultantProfile.contactPerson.phone?.phoneCode ||
        contactPersonOriginalProfile.phone?.phonePrefix !== draft!.consultantProfile.contactPerson.phone?.phonePrefix ||
        contactPersonOriginalProfile.phone?.phoneNumber !==
          draft!.consultantProfile.contactPerson.phone?.phoneNumber)) ??
    false;

  const sortedDraftWorkStyles = draft?.workStyles?.sort((a, b) => (a.type > b.type ? 1 : -1));
  const sortedProfileWorkStyles = originalProfile?.workStyles?.sort((a, b) => (a.type > b.type ? 1 : -1));
  const workStylesChanged = Boolean(
    jobRequest.workStyles?.length && JSON.stringify(sortedDraftWorkStyles) !== JSON.stringify(sortedProfileWorkStyles),
  );

  const showPaymentError = useCallback(
    (errorCode: string) => {
      setPaymentError(errorCode);

      if (modalRef.current && paymentRef.current) {
        const top = paymentRef.current.offsetTop - 140;
        modalRef.current.scrollTo({ top, behavior: "smooth" });
      }
    },
    [modalRef, paymentRef],
  );

  useEffect(() => {
    const initData = async () => {
      const originalProfile = await fetchConsultantProfile({
        consultantProfileId: profile!.parent ? profile!.parent.id : profile!.id!,
        companyId: contextCompany?.id,
      });

      let contactPersonOriginalProfile: ConsultantProfile | "NONE";
      try {
        if (originalProfile.id !== draft!.consultantProfile.contactPerson.id) {
          contactPersonOriginalProfile = await fetchConsultantProfile({
            consultantProfileId: draft!.consultantProfile.contactPerson.id,
            companyId: contextCompany?.id,
          });
        } else {
          contactPersonOriginalProfile = originalProfile;
        }
      } catch (error) {
        contactPersonOriginalProfile = "NONE";
      }

      setOriginalProfile(originalProfile);
      setContactPersonOriginalProfile(contactPersonOriginalProfile);
      setDraft(d => ({ ...d!, step: maxJobApplicationStepKey(draft?.step, "SUMMARY") }));
    };

    if (!originalProfile) {
      initData();
    }
  });

  const handleApplyButtonClick = useCallback(
    async (values: SummaryFormValues) => {
      try {
        const submittedApplication = await confirmJobApplication(
          {
            jobApplicationId: draft!.id,
            companyId: contextCompany?.id,
          },
          {
            ...transformDraftWithSummary(values)(draft!),
            step: maxJobApplicationStepKey(draft?.step, "SUMMARY"),
          },
        );
        setDraft(submittedApplication);

        sendEvent({
          type: "apply_job_complete",
          id: jobRequest.id,
          name: jobRequest.title!,
          location: formatMultipleLocations(t, jobRequest.locations, jobRequest.locations?.length),
        });

        if (!initialProfile) {
          const user = await refresh();
          updateContextUser(user);
        }
      } catch (error) {
        let errorMessage = t("common:ERROR_MESSAGES.GENERAL");
        switch (error?.payload?.code) {
          case "INVALID_TYPE":
          case "NOT_VALID_PARAM":
            errorMessage = t("JOB_REQUESTS.APPLY.WIZARD.WRONG_DATA_MESSAGE");
            break;
          case "JRA_TASK_CLOSED":
            errorMessage = t("JOB_REQUESTS.APPLY.WIZARD.JRA_TASK_CLOSED_DATA_MESSAGE");
            break;
          case "JR_CLOSED":
            errorMessage = t("JOB_REQUESTS.APPLY.WIZARD.JR_CLOSED_DATA_MESSAGE");
            break;
          case "DEACTIVATED_CONTACT_PERSON":
            errorMessage = t("JOB_REQUESTS.APPLY.WIZARD.CONTACT_PERSON_DEACTIVATED");
            break;
          case "INVALID_COST_VERIFICATION":
          case "UNAVAILABLE_PAYEXPRESS":
            showPaymentError(error.payload.code);
            return;
        }
        addToast({
          text: errorMessage,
          type: "danger",
        });
      }
    },
    [
      draft,
      setDraft,
      contextCompany,
      addToast,
      t,
      showPaymentError,
      initialProfile,
      jobRequest,
      sendEvent,
      updateContextUser,
    ],
  );

  const finalValidationSchema = contextCompany ? summaryCompanyValidation : summaryValidation;

  return (
    <Box py="md">
      <Container variant="narrow">
        <Flex px="sm" flexDirection="column" gap="sm">
          <ApplyWizardBackButton />

          <Heading size="h-md">{t("JOB_REQUESTS.APPLY.WIZARD.SUMMARY")}</Heading>

          <Flex flexDirection="column" gap="md">
            <ResponsiveCard sx={{ borderBottomWidth: [0, null, null, 1] }}>
              <Flex gap="xs" alignItems="center">
                <LazyUserAvatar user={draft!.consultantProfile} size="lg" />
                <Heading data-test="user-fullName" size="h-sm">
                  {formatFullName(draft!.consultantProfile)}
                </Heading>
              </Flex>

              <Flex flexDirection="column" py="md" gap="sm">
                <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
                  <Grid>
                    <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.NOTICE_PERIOD")}</Paragraph>
                  </Grid>
                  <Grid>
                    <Paragraph data-test="availability">
                      {draft!.consultantProfile.availability &&
                        (draft!.consultantProfile.availabilityDate
                          ? formatDate(draft!.consultantProfile.availabilityDate)
                          : tCodelist("availability", draft!.consultantProfile.availability))}
                    </Paragraph>
                  </Grid>
                </Grid>
                <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
                  <Grid>
                    <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.MOTIVATION")}</Paragraph>
                  </Grid>
                  <Grid>
                    <Paragraph data-test="motivation">
                      {draft!.motivation || t("JOB_APPLICATIONS.DETAIL.EMPTY")}
                    </Paragraph>
                  </Grid>
                </Grid>
              </Flex>

              <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

              <Flex flexDirection="column" gap="sm" py="md">
                <Heading size="h-sm">
                  {t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.APPLICATION_CONTACT`)}
                </Heading>

                <PopoverDisclosure as={Paragraph} bold {...contactPersonPopoverState}>
                  <Flex gap="xs" alignItems="center">
                    <LazyUserAvatar user={draft!.consultantProfile.contactPerson} size="xs" />
                    <Paragraph data-test="contact-fullName" bold>
                      {formatFullName(draft!.consultantProfile.contactPerson)}
                    </Paragraph>
                  </Flex>
                </PopoverDisclosure>

                <UserPopover
                  user={draft!.consultantProfile.contactPerson}
                  state={contactPersonPopoverState}
                  position={draft!.consultantProfile.contactPerson.position?.name}
                />
              </Flex>

              <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

              <JobApplicationPaymentDetailsSummary
                ref={paymentRef}
                jobRequest={jobRequest}
                jobApplication={draft!}
                paymentError={paymentError}
              />

              {draft!.consultantProfile.cvs && draft!.consultantProfile.cvs.length > 0 && (
                <React.Fragment>
                  <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

                  <Flex flexDirection="column" gap="sm" py="md">
                    <Heading size="h-sm">{t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUMMARY.CV`)}</Heading>
                    <Grid columns="1fr">
                      {draft!.consultantProfile.cvs.map(cv => (
                        <Grid key={cv.systemId}>
                          <PreviewFileEntry attachment={cv}>{cv.name}</PreviewFileEntry>
                        </Grid>
                      ))}
                    </Grid>
                  </Flex>
                </React.Fragment>
              )}

              {draft!.attachments && draft!.attachments.length > 0 && (
                <React.Fragment>
                  <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

                  <Flex flexDirection="column" gap="sm" py="md">
                    <Heading size="h-sm">
                      {t(`JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.SUMMARY.OTHER_DOCUMENTS`)}
                    </Heading>
                    <Grid columns="1fr">
                      {draft!.attachments.map(attachment => (
                        <Grid key={attachment.systemId}>
                          <PreviewFileEntry attachment={attachment}>{attachment.name}</PreviewFileEntry>
                        </Grid>
                      ))}
                    </Grid>
                  </Flex>
                </React.Fragment>
              )}

              {draft!.answers && draft!.answers.length > 0 && (
                <React.Fragment>
                  <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

                  <Flex flexDirection="column" gap="sm" py="md">
                    <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.QUESTIONNAIRE.SECTION_HEADER")}</Heading>
                    {draft!.answers.map((answer, index) => (
                      <SingleAnswer key={index} answer={answer} />
                    ))}
                  </Flex>
                </React.Fragment>
              )}

              {draft!.workStyles && draft!.workStyles.length > 0 && (
                <React.Fragment>
                  <CardHorizontalDivider sx={{ my: 0 }} size="wide" />

                  <Flex flexDirection="column" gap="sm" py="md">
                    <Heading size="h-sm">{t("CONSULTANT_PROFILES.DETAIL.WORK_STYLES.SECTION_HEADER")}</Heading>

                    <Flex flexDirection="column" gap="xs">
                      {draft!.workStyles
                        ?.sort((a, b) => b.score - a.score)
                        .map(workStyle => (
                          <SingleWorkStyle
                            key={workStyle.type}
                            WorkStyleIcon={WORKSTYLES_ICON_MAP[workStyle.type]}
                            {...workStyle}
                          />
                        ))}
                    </Flex>
                  </Flex>
                </React.Fragment>
              )}
            </ResponsiveCard>

            {originalProfile && contactPersonOriginalProfile && (
              <Formik
                initialValues={{
                  consentApproved: false,
                  updateCvs: cvsChanged && !originalProfile.cvs.length,
                  updateAvailability: availabilityChanged,
                  completeProfileConsentApproved: false,
                  updateWorkstyles: workStylesChanged && !originalProfile.workStyles?.length,
                  updateContactPersonPhoneNumber:
                    contactPersonPhoneNumberChanged &&
                    contactPersonOriginalProfile !== "NONE" &&
                    !contactPersonOriginalProfile.phone,
                }}
                validationSchema={finalValidationSchema}
                onSubmit={handleApplyButtonClick}>
                {(formikProps: FormikProps<SummaryFormValues>) => (
                  <ApplySummaryForm
                    formik={formikProps}
                    jobRequest={jobRequest}
                    cvsChanged={cvsChanged}
                    availabilityChanged={availabilityChanged}
                    contactPersonPhoneNumberChanged={contactPersonPhoneNumberChanged}
                    workStylesChanged={workStylesChanged}
                  />
                )}
              </Formik>
            )}
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};
